/**
 * AuthenticatedApp - Shell para usuarios autenticados
 *
 * Maneja toda la lógica y UI para usuarios autenticados:
 * - Carga de datos inicial (conversaciones, documentos)
 * - Coordinación de paneles y navegación
 * - Event bus subscriptions
 * - MainLayout rendering
 *
 * Extraído de AppShell.tsx siguiendo Clean Code principles
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import type { User } from '../types/user';
import { MainLayout } from './MainLayout';
import { useUIState } from '../contexts/UIStateContext';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import { useNavigationStack } from '../hooks/useNavigationStack';
import { useConversationManagement } from '../hooks/useConversationManagement';
import { useDocumentManagement } from '../hooks/useDocumentManagement';
import { useMessageManagement } from '../hooks/useMessageManagement';
import { useViewerManagement } from '../hooks/useViewerManagement';
import { usePanelManagement } from '../hooks/usePanelManagement';
import { eventBus, EventNames } from '../utils/eventBus';
import logger from '../utils/simpleLogger';
import { apiService } from '../services/api';
import ViewSavedItemModal from './ViewSavedItemModal';

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL || (import.meta.env.DEV ? 'http://localhost:8001/api' : '/api')
).replace(/\/$/, '');

interface AuthenticatedAppProps {
  currentUser: User;
  onLogout: () => void;
  onRefreshUser?: () => Promise<void>;
}

export function AuthenticatedApp({ currentUser, onLogout, onRefreshUser }: AuthenticatedAppProps) {
  // UI State
  const uiState = useUIState();

  // Navigation
  const navigationStack = useNavigationStack();

  // Document selection (from context)
  const { selectedDocuments } = useDocumentSelection();

  // Conversation management
  const conversationManager = useConversationManagement(currentUser.id);
  const {
    conversations,
    currentConversation,
    isLoading: conversationsLoading,
    loadConversations,
    selectConversation,
    createConversation,
    updateConversation,
    deleteConversation,
    duplicateConversation,
  } = conversationManager;

  // Document management
  const docs = useDocumentManagement();
  const {
    documents,
    selectedDocuments: selectedDocIds,
    isLoading: documentsLoading,
    loadDocuments,
  } = docs;

  // Error state for message management
  const [messageError, setMessageError] = React.useState<string | null>(null);

  // Drive fullscreen state (not in panel manager, specific to viewer interaction)
  const [isDriveFullscreen, setIsDriveFullscreen] = React.useState(false);

  // App state (error and success messages)
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const appState = React.useMemo(
    () => ({
      error,
      successMessage,
      clearError: () => setError(null),
      clearSuccessMessage: () => setSuccessMessage(null),
      setSuccessMessage,
      setError,
    }),
    [error, successMessage]
  );

  // Profile/Password modal state
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);

  // Saved snippet state
  const [savedSnippetId, setSavedSnippetId] = React.useState<string | null>(null);

  // View saved item modal state
  const [viewSavedItem, setViewSavedItem] = useState<{
    item: any;
    type: 'message' | 'snippet';
  } | null>(null);

  // Panel management
  const panelManager = usePanelManagement();

  // Navigation helper function (uses ref for stable callback)
  const navigateToView = useCallback((view: any, data?: any, label?: string) => {
    navigationStackRef.current.push({ view, data, label });
  }, []);

  // Message management
  const messageManager = useMessageManagement({
    currentConversation,
    conversations,
    selectConversation,
    loadConversations,
    createConversation,
    setError: setMessageError,
  });

  // Viewer management
  const viewerManager = useViewerManagement({
    navigateToView,
    isDriveFullscreen,
    setIsDriveFullscreen,
    setIsDriveOpen: panelManager.setIsDriveOpen,
  });

  // Refs for stable access in event handlers (avoid re-subscribing EventBus)
  const onLogoutRef = useRef(onLogout);
  const navigationStackRef = useRef(navigationStack);
  onLogoutRef.current = onLogout;
  navigationStackRef.current = navigationStack;

  // Load initial data on mount (runs once — loadConversations/loadDocuments are stable callbacks)
  useEffect(() => {
    logger.info('AuthenticatedApp: Loading initial data');
    loadConversations();
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Event bus subscriptions (stable — uses refs to avoid re-subscribing)
  useEffect(() => {
    const handleLogout = () => {
      logger.info('AuthenticatedApp: Logout event received');
      onLogoutRef.current();
    };

    const handleNavigate = (data: any) => {
      if (data?.view && navigationStackRef.current.currentState) {
        navigationStackRef.current.push({ view: data.view });
      }
    };

    eventBus.on('user-logout' as any, handleLogout);
    eventBus.on('navigate-to-view' as any, handleNavigate);

    return () => {
      eventBus.off('user-logout' as any, handleLogout);
      eventBus.off('navigate-to-view' as any, handleNavigate);
    };
  }, []);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      logger.info(`AuthenticatedApp: Selecting conversation ${conversationId}`);
      const conversation = conversations.find((c) => c.id === conversationId);
      selectConversation(conversation || null);
    },
    [selectConversation, conversations]
  );

  // Handle new conversation (reset to blank state, auto-naming on first message)
  const handleNewConversation = useCallback(() => {
    logger.info('AuthenticatedApp: New conversation (blank state)');
    selectConversation(null);
    messageManager.setMessages([]);
    // Reset Drive filters so stale state doesn't leak into the new conversation
    eventBus.emit(EventNames.NEW_CONVERSATION);
  }, [selectConversation, messageManager]);

  // Handle conversation creation
  const handleCreateConversation = useCallback(
    async (name: string, description?: string) => {
      logger.info('AuthenticatedApp: Creating conversation', { name });
      try {
        const newConversation = await createConversation(name);
        if (newConversation) {
          // Prevent useMessageManagement from auto-loading messages for this brand-new
          // conversation. Without this, the auto-load effect fetches 0 messages from the API
          // (since the conversation was just created) and wipes out optimistic/streaming messages,
          // causing the empty state to flash.
          messageManager.markConversationAsLoaded(newConversation.id);
        }
        return newConversation;
      } catch (err) {
        logger.error('Error in handleCreateConversation:', err);
        return null;
      }
    },
    [createConversation, messageManager]
  );

  // Handle conversation update (rename, pin, archive)
  const handleUpdateConversation = useCallback(
    async (
      conversationId: string,
      updates: { name?: string; is_pinned?: boolean; is_active?: boolean }
    ): Promise<boolean> => {
      logger.info(`AuthenticatedApp: Updating conversation ${conversationId}`, updates);
      return updateConversation(conversationId, updates);
    },
    [updateConversation]
  );

  // Handle conversation deletion
  const handleDeleteConversation = useCallback(
    async (conversationId: string): Promise<boolean> => {
      logger.info(`AuthenticatedApp: Deleting conversation ${conversationId}`);
      return deleteConversation(conversationId);
    },
    [deleteConversation]
  );

  // Handle conversation duplication
  const handleDuplicateConversation = useCallback(
    async (conversationId: string) => {
      logger.info(`AuthenticatedApp: Duplicating conversation ${conversationId}`);
      return duplicateConversation(conversationId);
    },
    [duplicateConversation]
  );

  // Profile/Password handlers
  const handleOpenProfile = useCallback(() => setShowProfileModal(true), []);
  const handleCloseProfile = useCallback(() => setShowProfileModal(false), []);
  const handleOpenChangePassword = useCallback(() => setShowChangePasswordModal(true), []);
  const handleCloseChangePassword = useCallback(() => setShowChangePasswordModal(false), []);
  const handleProfileUpdate = useCallback(
    async (updatedUser: any) => {
      logger.info('AuthenticatedApp: Profile updated', { userId: updatedUser?.id });
      if (onRefreshUser) {
        await onRefreshUser();
      }
      setShowProfileModal(false);
    },
    [onRefreshUser]
  );

  // Navigation handlers (uses ref for stable callback)
  const handleNavigateBack = useCallback(() => {
    const previousState = navigationStackRef.current.pop();
    if (previousState) {
      logger.info('AuthenticatedApp: Navigated back', { view: previousState.view });
    }
  }, []);

  // Document handlers
  const handleDocumentSelect = useCallback(
    (documentIds: string | string[]) => {
      const ids = Array.isArray(documentIds) ? documentIds : [documentIds];
      docs.setSelectedDocuments(ids);
    },
    [docs]
  );

  const handleDocumentDelete = useCallback(
    async (documentId: string): Promise<void> => {
      await docs.deleteDocument(documentId);
    },
    [docs]
  );

  const handleDocumentReprocess = useCallback(
    async (documentId: string): Promise<void> => {
      logger.info('AuthenticatedApp: Reprocessing document', { documentId });
      try {
        await apiService.reprocessDocument(documentId);
        logger.info('AuthenticatedApp: Document reprocessing started', { documentId });

        // Reload documents to show updated status (processing)
        await loadDocuments();

        // Connect to SSE stream for real-time status updates
        const token = localStorage.getItem('access_token');
        if (!token) {
          logger.warn('AuthenticatedApp: No access token found for SSE connection');
          return;
        }
        const eventSource = new EventSource(
          `${API_BASE}/documents/${documentId}/status-stream?token=${token}`
        );

        eventSource.addEventListener('status_update', (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          logger.info('AuthenticatedApp: Document status update received', {
            documentId,
            status: data.processing_status,
          });
          // Reload documents to show updated status
          loadDocuments();
        });

        eventSource.addEventListener('processing_complete', async (event: MessageEvent) => {
          const data = JSON.parse(event.data);
          logger.info('AuthenticatedApp: Document processing complete', {
            documentId,
            status: data.processing_status,
          });
          // Final reload
          await loadDocuments();
          eventSource.close();
        });

        eventSource.addEventListener('error', (event: Event) => {
          logger.error('AuthenticatedApp: SSE error for document', { documentId, event });
          eventSource.close();
        });

        eventSource.addEventListener('timeout', (event: MessageEvent) => {
          logger.warning('AuthenticatedApp: SSE timeout for document', { documentId });
          eventSource.close();
          loadDocuments(); // Final reload
        });
      } catch (error) {
        logger.error('AuthenticatedApp: Error reprocessing document', { documentId, error });
        throw error;
      }
    },
    [loadDocuments]
  );

  // Message handlers
  // Removed handleChatMessageSave - ChatPanel will handle saving directly via apiService

  const handleSavedMessageView = useCallback((savedMessage: any) => {
    logger.info('AuthenticatedApp: Opening saved message view', { id: savedMessage?.id });
    setViewSavedItem({ item: savedMessage, type: 'message' });
  }, []);

  // Render MainLayout with all props
  return (
    <>
      <MainLayout
        // Auth
        currentUser={currentUser}
        onLogout={onLogout}
        onOpenProfile={handleOpenProfile}
        onOpenChangePassword={handleOpenChangePassword}
        onCloseProfile={handleCloseProfile}
        onCloseChangePassword={handleCloseChangePassword}
        showProfileModal={showProfileModal}
        showChangePasswordModal={showChangePasswordModal}
        onProfileUpdate={handleProfileUpdate}
        // Navigation
        mainView={navigationStack.currentState.view}
        navigateToView={navigateToView}
        handleNavigateBack={handleNavigateBack}
        navigation={navigationStack}
        // Panels
        isDriveOpen={panelManager.isDriveOpen}
        handleOpenDrive={panelManager.handleOpenDrive}
        setIsDriveOpen={panelManager.setIsDriveOpen}
        // Viewer
        activeViewer={viewerManager.activeViewer}
        setActiveViewer={viewerManager.setActiveViewer}
        openInViewer={viewerManager.openInViewer}
        handleStudioResultView={viewerManager.handleStudioResultView}
        // Conversations
        conversations={conversations}
        currentConversation={currentConversation}
        onConversationCreate={handleCreateConversation}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onUpdateConversation={handleUpdateConversation}
        onDeleteConversation={handleDeleteConversation}
        onDuplicateConversation={handleDuplicateConversation}
        // Documents
        documents={documents}
        selectedDocIds={selectedDocIds}
        setSelectedDocuments={docs.setSelectedDocuments}
        loadDocuments={loadDocuments}
        handleDocumentSelect={handleDocumentSelect}
        handleDocumentDelete={handleDocumentDelete}
        handleDocumentReprocess={handleDocumentReprocess}
        // Messages
        messages={messageManager.messages}
        setMessages={messageManager.setMessages}
        handleConversationCleared={messageManager.handleConversationCleared}
        handleSavedMessageView={handleSavedMessageView}
        // App State
        appState={appState}
        // UI State
        savedSnippetId={savedSnippetId}
        setSavedSnippetId={setSavedSnippetId}
      />
      {viewSavedItem && (
        <ViewSavedItemModal
          isOpen={true}
          onClose={() => setViewSavedItem(null)}
          item={viewSavedItem.item}
          itemType={viewSavedItem.type}
        />
      )}
    </>
  );
}
